/**
 * WHO IS SENDING THE QUOTE.
 *
 * The same six fields on every quote a company ever writes, so they are typed
 * once and kept, not per job. A contractor who has to retype his phone number
 * on every estimate stops putting his phone number on estimates.
 *
 * Kept in this browser, alongside the light defaults, for the same reason: it
 * is a preference about how this person works, not a fact about a house. It
 * never goes into a document, an export or the network.
 */

import { currentAccountId } from './account'

export interface Company {
  name: string
  phone: string
  email: string
  /** Street or city line. One line, because a quote header is not a letter. */
  address: string
  /** State license, insurance number, whatever has to appear. Optional. */
  license: string
  /**
   * The letters in front of every document number, so LP-1001. Shops set
   * their own initials; this is only what it says before anybody does.
   */
  prefix: string
  /**
   * THE NEXT NUMBER OFF THE PAD.
   *
   * A quote book is numbered in sequence and the numbers are how a job is
   * found again, so the app hands out the next one rather than asking. It
   * lives here beside the letterhead because it belongs to the company, not
   * to any one job.
   */
  nextNumber: number
  /** A data URL, so it survives a reload without a server. */
  logoDataUrl: string | null
}

export const emptyCompany = (): Company => ({
  name: '',
  phone: '',
  email: '',
  address: '',
  license: '',
  prefix: 'LP',
  nextNumber: 1001,
  logoDataUrl: null,
})

/*
 * The letterhead belongs to a company, not to a browser, so it is filed under
 * the account. OLD is where it lived before accounts existed; it is read once
 * and carried across, because the alternative is a contractor opening the app
 * one morning to find his logo gone.
 */
const OLD = 'lightsos.company.v1'
const keyFor = () => `lightsos.company.${currentAccountId()}`

export function loadCompany(): Company {
  try {
    const raw = localStorage.getItem(keyFor()) ?? localStorage.getItem(OLD)
    if (!raw) return emptyCompany()
    const saved = JSON.parse(raw) as Partial<Company>
    // Merged over the empty one, so a field added later is never undefined.
    return { ...emptyCompany(), ...saved }
  } catch {
    return emptyCompany()
  }
}

export function saveCompany(c: Company) {
  try {
    localStorage.setItem(keyFor(), JSON.stringify(c))
  } catch {
    /* private mode: it just does not persist */
  }
}

/**
 * Tear the next number off the pad, and hand back the company with the
 * counter moved on. Pure, so the caller decides when it is actually spent:
 * a number is burned the moment it is handed out, and burning one on a screen
 * somebody opened and closed is how quote books end up with gaps.
 */
export function nextDocNumber(c: Company): { number: string; company: Company } {
  const n = Number.isFinite(c.nextNumber) && c.nextNumber > 0 ? Math.round(c.nextNumber) : 1001
  return { number: String(n), company: { ...c, nextNumber: n + 1 } }
}

/** True when there is enough to put a header on a quote. */
export const hasCompany = (c: Company) => !!(c.name.trim() || c.logoDataUrl)
